"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.PlumTreeManager = void 0;
const dht_1 = require("@streamr/dht");
const NetworkRpc_1 = require("../../../generated/packages/trackerless-network/protos/NetworkRpc");
const PlumTreeRpcLocal_1 = require("./PlumTreeRpcLocal");
const PlumTreeRpcRemote_1 = require("./PlumTreeRpcRemote");
const NetworkRpc_client_1 = require("../../../generated/packages/trackerless-network/protos/NetworkRpc.client");
const eventemitter3_1 = __importDefault(require("eventemitter3"));
const utils_1 = require("@streamr/utils");
const ContentDeliveryRpcRemote_1 = require("../ContentDeliveryRpcRemote");
const PausedNeighbors_1 = require("./PausedNeighbors");
const logger = new utils_1.Logger(module);
class PlumTreeManager extends eventemitter3_1.default {
    neighbors;
    localPeerDescriptor;
    // We have paused sending real data to these neighbrs and only send metadata
    localPausedNeighbors = new PausedNeighbors_1.PausedNeighbors();
    // We have asked these nodes to pause sending real data to us, used to limit sending of pausing and resuming requests
    remotePausedNeighbors = new PausedNeighbors_1.PausedNeighbors();
    rpcLocal;
    latestMessages = new Map();
    rpcCommunicator;
    metadataTimestampsAheadOfRealData = new Map();
    constructor(options) {
        super();
        this.neighbors = options.neighbors;
        this.localPeerDescriptor = options.localPeerDescriptor;
        this.rpcLocal = new PlumTreeRpcLocal_1.PlumTreeRpcLocal(this.neighbors, this.localPausedNeighbors, (metadata, previousNode) => this.onMetadata(metadata, previousNode), (fromTimestamp, msgChainId, remotePeerDescriptor) => this.sendBuffer(fromTimestamp, msgChainId, remotePeerDescriptor));
        this.neighbors.on('nodeRemoved', (nodeId) => this.onNeighborRemoved(nodeId));
        this.rpcCommunicator = options.rpcCommunicator;
        this.rpcCommunicator.registerRpcNotification(NetworkRpc_1.MessageID, 'sendMetadata', (msg, context) => this.rpcLocal.sendMetadata(msg, context));
        this.rpcCommunicator.registerRpcNotification(NetworkRpc_1.PauseNeighborRequest, 'pauseNeighbor', (msg, context) => this.rpcLocal.pauseNeighbor(msg, context));
        this.rpcCommunicator.registerRpcNotification(NetworkRpc_1.ResumeNeighborRequest, 'resumeNeighbor', (msg, context) => this.rpcLocal.resumeNeighbor(msg, context));
    }
    async pauseNeighbor(node, msgChainId) {
        if (this.neighbors.has((0, dht_1.toNodeId)(node))) {
            logger.debug(`Pausing neighbor ${(0, dht_1.toNodeId)(node)}`);
            this.remotePausedNeighbors.add((0, dht_1.toNodeId)(node), msgChainId);
            const remote = this.createRemote(node);
            await remote.pauseNeighbor(msgChainId);
        }
    }
    async resumeNeighbor(node, msgChainId, fromTimestamp) {
        if (this.remotePausedNeighbors.isPaused((0, dht_1.toNodeId)(node), msgChainId)) {
            logger.debug(`Resuming neighbor ${(0, dht_1.toNodeId)(node)}`);
            this.remotePausedNeighbors.delete((0, dht_1.toNodeId)(node), msgChainId);
            const remote = this.createRemote(node);
            await remote.resumeNeighbor(fromTimestamp, msgChainId);
        }
    }
    onNeighborRemoved(nodeId) {
        this.localPausedNeighbors.deleteAll(nodeId);
        this.remotePausedNeighbors.deleteAll(nodeId);
    }
    getLatestMessageTimestamp(msgChainId) {
        if (!this.latestMessages.has(msgChainId) || this.latestMessages.get(msgChainId).length === 0) {
            return 0;
        }
        return this.latestMessages.get(msgChainId)[this.latestMessages.get(msgChainId).length - 1].messageId.timestamp;
    }
    async sendBuffer(fromTimestamp, msgChainId, neighbor) {
        const remote = new ContentDeliveryRpcRemote_1.ContentDeliveryRpcRemote(this.localPeerDescriptor, neighbor, this.rpcCommunicator, NetworkRpc_client_1.ContentDeliveryRpcClient);
        const messages = this.latestMessages.get(msgChainId)?.filter((msg) => msg.messageId.timestamp > fromTimestamp) ?? [];
        await Promise.all(messages.map((msg) => remote.sendStreamMessage(msg)));
    }
    async onMetadata(msg, previousNode) {
        // If we receive newer metadata than messages in the buffer, resume the sending neighbor
        const latestMessageTimestamp = this.getLatestMessageTimestamp(msg.messageChainId);
        if (latestMessageTimestamp < msg.timestamp) {
            if (!this.metadataTimestampsAheadOfRealData.has(msg.messageChainId)) {
                this.metadataTimestampsAheadOfRealData.set(msg.messageChainId, new Set());
            }
            this.metadataTimestampsAheadOfRealData.get(msg.messageChainId).add(msg.timestamp);
            if (this.metadataTimestampsAheadOfRealData.get(msg.messageChainId).size > 1) {
                await this.resumeNeighbor(previousNode, msg.messageChainId, this.getLatestMessageTimestamp(msg.messageChainId));
                this.metadataTimestampsAheadOfRealData.get(msg.messageChainId).forEach((timestamp) => {
                    this.metadataTimestampsAheadOfRealData.get(msg.messageChainId).delete(timestamp);
                });
            }
        }
    }
    createRemote(neighbor) {
        return new PlumTreeRpcRemote_1.PlumTreeRpcRemote(this.localPeerDescriptor, neighbor, this.rpcCommunicator, NetworkRpc_client_1.PlumTreeRpcClient);
    }
    broadcast(msg, previousNode) {
        const messageChainId = msg.messageId.messageChainId;
        if (!this.latestMessages.has(messageChainId)) {
            this.latestMessages.set(messageChainId, []);
        }
        if (this.latestMessages.get(messageChainId).length < 20) {
            this.latestMessages.get(messageChainId).push(msg);
        }
        else {
            this.latestMessages.get(messageChainId).shift();
            this.latestMessages.get(messageChainId).push(msg);
        }
        if (this.metadataTimestampsAheadOfRealData.has(msg.messageId.messageChainId)) {
            this.metadataTimestampsAheadOfRealData.get(msg.messageId.messageChainId).delete(msg.messageId.timestamp);
        }
        this.emit('message', msg);
        const neighbors = this.neighbors.getAll().filter((neighbor) => (0, dht_1.toNodeId)(neighbor.getPeerDescriptor()) !== previousNode);
        for (const neighbor of neighbors) {
            if (this.localPausedNeighbors.isPaused((0, dht_1.toNodeId)(neighbor.getPeerDescriptor()), msg.messageId.messageChainId)) {
                const remote = this.createRemote(neighbor.getPeerDescriptor());
                setImmediate(() => remote.sendMetadata(msg.messageId));
            }
            else {
                setImmediate(() => neighbor.sendStreamMessage(msg));
            }
        }
    }
    isNeighborPaused(node, msgChainId) {
        return this.localPausedNeighbors.isPaused((0, dht_1.toNodeId)(node), msgChainId)
            || this.remotePausedNeighbors.isPaused((0, dht_1.toNodeId)(node), msgChainId);
    }
    stop() {
        this.neighbors.off('nodeRemoved', this.onNeighborRemoved);
    }
}
exports.PlumTreeManager = PlumTreeManager;
//# sourceMappingURL=PlumTreeManager.js.map